import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-[13.5px] font-medium tracking-tight ring-offset-background transition-[color,background-color,border-color,box-shadow,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/92 hover:shadow-[var(--shadow-primary)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm",
        destructive:
          "bg-destructive text-white shadow-sm hover:bg-destructive/92 hover:shadow-lg hover:shadow-destructive/35 hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm",
        outline:
          "border border-border/80 bg-card text-foreground hover:border-primary hover:text-primary hover:shadow-[var(--shadow-primary-soft)] active:shadow-none",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/85 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm",
        ghost: "hover:bg-primary/8 hover:text-primary",
        link: "rounded-lg text-primary underline-offset-4 hover:underline shadow-none",
        success:
          "bg-success text-white shadow-sm hover:bg-success/92 hover:shadow-lg hover:shadow-success/35 hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm",
        warning:
          "bg-warning text-white shadow-sm hover:bg-warning/92 hover:shadow-lg hover:shadow-warning/35 hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm",
      },
      size: {
        default: "h-10 px-6 py-2",
        sm: "h-9 px-4 text-xs rounded-lg",
        lg: "h-11 px-8 text-[14px] rounded-xl",
        icon: "h-10 w-10 gap-0 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
